import axios from 'axios';

import { useAuthStore } from '@/stores/authStore';
import type { ApiResponse, ChatRequest, ChatResponse, PublicPlan, PublicTenant } from '@/types/api';
import { logFrontendError } from '@/utils/logger';
import { tenantManager } from '@/utils/tenantManager';
import { resolveApiBaseUrl } from '@/utils/apiBase';

const baseURL = resolveApiBaseUrl();

const getSlugFromToken = (token: string | null): string | null => {
  if (!token) return null;
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return (payload as { tenantSlug?: string }).tenantSlug ?? null;
  } catch {
    return null;
  }
};

export const api = axios.create({
  baseURL,
  withCredentials: true,
});

export const publicApi = axios.create({
  baseURL,
  withCredentials: true,
});

api.interceptors.request.use((config) => {
  const token = useAuthStore.getState().accessToken;
  // Prioridade: slug da URL/subdomínio → slug do JWT (cobre demo e tenants autenticados)
  const tenantSlug = tenantManager.getSlug() ?? getSlugFromToken(token);
  if (token && config.headers) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  // Não enviar X-Tenant-Slug para rotas de auth, pois elas usam fallback
  if (tenantSlug && config.headers && !config.url?.includes('/auth/')) {
    config.headers['X-Tenant-Slug'] = tenantSlug;
  }
  return config;
});

publicApi.interceptors.request.use((config) => {
  // Não enviar X-Tenant-Slug para rotas públicas
  const tenantSlug = tenantManager.getSlug();
  if (tenantSlug && config.headers && !config.url?.includes('/public/')) {
    config.headers['X-Tenant-Slug'] = tenantSlug;
  }
  return config;
});

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    // Tratamento específico para erros de rede
    if (!error.response) {
      // Erro de rede (sem resposta do servidor)
      const networkError = {
        message: error.message || 'Erro de conexão',
        code: error.code,
        isNetworkError: true,
      };

      void logFrontendError({
        message: 'Network error',
        stack: error.stack,
        context: {
          url: error.config?.url,
          method: error.config?.method,
          error: networkError,
        },
      });

      // Rejeitar com erro mais descritivo
      return Promise.reject({
        ...error,
        isNetworkError: true,
        message: 'Erro de conexão. Verifique sua internet e tente novamente.',
      });
    }

    const context = {
      url: error.config?.url,
      method: error.config?.method,
      status: error.response?.status,
      response: error.response?.data,
    };

    void logFrontendError({
      message: error.message ?? 'HTTP error',
      stack: error.stack,
      context,
    });

    if (error.response?.status === 401) {
      // Não redirecionar se já estiver na página de login ou se for uma rota pública
      const currentPath = typeof window !== 'undefined' ? window.location.pathname : '';
      const isPublicRoute = currentPath === '/login' || currentPath === '/' || currentPath.startsWith('/help');
      
      // Não redirecionar se for erro em rota de billing (pode ser acesso bloqueado, não necessariamente não autenticado)
      const isBillingRoute = error.config?.url?.includes('/billing');
      
      if (!isPublicRoute && !isBillingRoute) {
        tenantManager.clear();
        const { clearAuth } = useAuthStore.getState();
        clearAuth();
        if (typeof window !== 'undefined') {
          window.location.replace('/login');
        }
      }
    }

    return Promise.reject(error);
  },
);

publicApi.interceptors.response.use(
  (response) => response,
  async (error) => {
    const context = {
      url: error.config?.url,
      method: error.config?.method,
      status: error.response?.status,
      response: error.response?.data,
    };

    void logFrontendError({
      message: error.message ?? 'HTTP error',
      stack: error.stack,
      context,
    });

    return Promise.reject(error);
  },
);

export const getPublicPlans = async () => {
  const response = await publicApi.get<ApiResponse<PublicPlan[]>>('/public/plans');
  return response.data.data;
};

export const sendPublicChatMessage = async (payload: ChatRequest) => {
  const response = await publicApi.post<ApiResponse<ChatResponse>>('/public/chat', payload);
  return response.data.data;
};

export const getPublicTenant = async () => {
  const response = await publicApi.get<ApiResponse<{ tenant: PublicTenant | null }>>(
    '/public/tenant',
  );
  return response.data.data.tenant;
};
